import { z } from "zod";

// Indexation (P8). Index status is derived from the same site: checks the DMCA
// engine runs — see migration 20261019120000_index_status.sql (view dmca_index +
// get_index_summary). Not in the generated Database types; read via structural
// casts in IndexationService.

export type IndexKind = "domain" | "url";
export type IndexStatus = "indexed" | "not_indexed" | null;

// DMCA verdict, as stored in dmca_targets.last_status. Deliberately NOT imported
// from types/dmca (that file is another session's zone) — the unified Indexation
// page reads DMCA columns through IndexationService, so the shape lives here.
export type DmcaStatusLike = "dmca" | "clean" | null;

// One row of the checker-error cause breakdown (get_index_summary).
export const errorGroupSchema = z.object({ cause: z.string(), n: z.coerce.number() });

export const indexSummarySchema = z.object({
  domains: z.coerce.number(),
  domains_indexed: z.coerce.number(),
  domains_not_indexed: z.coerce.number(),
  domains_unchecked: z.coerce.number(),
  urls: z.coerce.number(),
  urls_indexed: z.coerce.number(),
  urls_not_indexed: z.coerce.number(),
  urls_unchecked: z.coerce.number(),
  // Deliberately-not-tracked tier-3 (migration 20261021100000). Optional so a
  // pre-migration summary still parses; z.object strips any other extra keys.
  domains_untracked: z.coerce.number().optional(),
  urls_untracked: z.coerce.number().optional(),
  // Risk-card fields (migration 20261027135127). All optional so a summary read
  // before that migration deploys still parses (fields read with `?? 0`).
  domains_flagged: z.coerce.number().optional(),
  domains_flagged_tier1: z.coerce.number().optional(),
  flagged_new_24h: z.coerce.number().optional(),
  flagged_new_7d: z.coerce.number().optional(),
  domains_bleeding: z.coerce.number().optional(),
  revenue_bleeding: z.coerce.number().optional(),
  domains_at_risk: z.coerce.number().optional(),
  revenue_at_risk: z.coerce.number().optional(),
  metrics_window_days: z.coerce.number().nullable().optional(),
  domains_tracked: z.coerce.number().optional(),
  domains_checked_24h: z.coerce.number().optional(),
  // Per-tier freshness (migration 20261029223318): each tier against its own
  // cadence (tier1 vs 24h, tier2 vs 7d), replacing the blended number in the UI.
  domains_tier1: z.coerce.number().optional(),
  domains_tier1_fresh: z.coerce.number().optional(),
  domains_tier2: z.coerce.number().optional(),
  domains_tier2_fresh: z.coerce.number().optional(),
  // Honest error split (migration 20261029223318): hard = real failures (alarm),
  // empty = Bright Data transient under-floor stub (neutral provider flakiness).
  errors_hard_24h: z.coerce.number().optional(),
  // Cause breakdown for the Errors tooltip (migration 20261029223318).
  errors_breakdown: z.array(errorGroupSchema).optional(),
  vip_total: z.coerce.number().optional(),
  vip_problem: z.coerce.number().optional(),
  // Range-scoped flow counts (migration 20261027181757). The summary RPC takes
  // [from,to]; these count the current episode's onset inside the window.
  domains_dropped_in_range: z.coerce.number().optional(),
  domains_flagged_in_range: z.coerce.number().optional(),
  urls_dropped_in_range: z.coerce.number().optional(),
  urls_flagged_in_range: z.coerce.number().optional(),
  // Dropped-in-range split by cause (migration 20261027193305): DMCA takedown vs
  // no takedown (organic/technical = investigate queue).
  domains_dropped_dmca_in_range: z.coerce.number().optional(),
  domains_dropped_clean_in_range: z.coerce.number().optional(),
  urls_dropped_dmca_in_range: z.coerce.number().optional(),
  urls_dropped_clean_in_range: z.coerce.number().optional(),
});

export type IndexSummary = z.infer<typeof indexSummarySchema>;

export const EMPTY_INDEX_SUMMARY: IndexSummary = {
  domains: 0,
  domains_indexed: 0,
  domains_not_indexed: 0,
  domains_unchecked: 0,
  urls: 0,
  urls_indexed: 0,
  urls_not_indexed: 0,
  urls_unchecked: 0,
};

// A row of dmca_targets, as read for the unified Indexation grid. Indexation is
// the backbone; the DMCA columns (last_status, notices, flagged_since…) and the
// nightly-materialized money metrics (m_*) extend it. The m_* figures are exact
// per grain (domain or url) — unlike the legacy `clicks` column, which copied a
// domain's clicks onto its URLs and must not be shown on url rows.
export interface IndexTargetRow {
  id: number;
  kind: IndexKind;
  target: string;
  domain: string;
  tier: 1 | 2 | 3;
  brand: string | null;
  geo: string | null;
  /** Legacy clicks column — used only for the default server ordering, never
   *  shown (it is wrong on url rows). The grid reads m_clicks instead. */
  clicks: number;
  index_status: IndexStatus;
  index_checked_at: string | null;
  // DMCA extension (dmca_targets).
  last_status: DmcaStatusLike;
  last_notices: number | null;
  last_notice_ids: string[];
  url_present: boolean | null;
  flagged_since: string | null;
  /** Onset of the current out-of-index episode (cleared on re-index). */
  index_dropped_since: string | null;
  error_streak: number;
  // Nightly-materialized money metrics, exact per grain. null = not computed yet.
  m_clicks: number | null;
  m_revenue: number | null;
  m_profit: number | null;
  m_conversions: number | null;
  m_leads: number | null;
  m_sales: number | null;
  m_metrics_at: string | null;
}

// Narrow health read off get_dmca_summary — "is the checker alive?" The unified
// page keeps the DMCA checker's health strip, but reads only these three fields
// (through IndexationService, not DmcaService, to stay inside this zone).
export const checkerHealthSchema = z.object({
  last_check_at: z.string().nullable(),
  checks_24h: z.coerce.number(),
  errors_24h: z.coerce.number(),
});

export type CheckerHealth = z.infer<typeof checkerHealthSchema>;

export const EMPTY_CHECKER_HEALTH: CheckerHealth = {
  last_check_at: null,
  checks_24h: 0,
  errors_24h: 0,
};

// One transition in a target's history (get_target_timeline). `indexed` is the
// index state after this check (null = unknown for a url with no url_present),
// `dmca` the DMCA verdict. The RPC already collapses runs to change-points.
export const indexTimelineEventSchema = z.object({
  at: z.string(),
  indexed: z.boolean().nullable(),
  dmca: z.enum(["dmca", "clean"]).nullable(),
  notice_ids: z.array(z.string()).default([]),
});
export const indexTimelineSchema = z.array(indexTimelineEventSchema);
export type IndexTimelineEvent = z.infer<typeof indexTimelineEventSchema>;
